/**
 * Croc Soft Fleet map marks — Dispatch Material Icons + Silk motion (2026-08 v8)
 * Icon = what was dispatched (person / UAV / UGV / cam / siren / mesh / job).
 * Motion = type-locked micro-anim on enroute/cover + continuous map lerp.
 * Soft≠Live.
 */

import { glyphForSoftUnit } from "./soft-fleet-scenes.js";
import { softLngLatInRadius } from "./ops-zone-fabric.js";

export const KIND_WORD = {
  person: "Responder",
  device: "Fixed edge",
  drone: "UAV",
  robot: "UGV",
  incident: "Job",
};

export const PROFILE_WORD = {
  walk: "foot path",
  bike: "ground path",
  drive: "road",
  air: "air corridor",
  static: "on station",
};

/**
 * Dual-tone marks — viewBox 0 0 32 32 · clean silhouette (2026-08 polish).
 * Moveable classes (CSS silk):
 *   sfm-spin · sfm-bob · sfm-pulse · sfm-scan · sfm-roll · sfm-beam · sfm-blink
 */
const MARK = {
  /* foot patrol — simple person silhouette */
  person: `
    <g class="sfm-bob">
      <circle class="sfm-fill" cx="16" cy="8.2" r="5.2"/>
      <circle class="sfm-body" cx="16" cy="8.2" r="4"/>
      <circle class="sfm-hi" cx="14.6" cy="7.1" r="1.15"/>
      <path class="sfm-fill" d="M8.5 20.4c1.2-3.4 3.8-5.1 7.5-5.1s6.3 1.7 7.5 5.1V23H8.5z"/>
      <path class="sfm-body" d="M9.6 20.2c1-3 3.2-4.4 6.4-4.4s5.4 1.4 6.4 4.4v1.6H9.6z"/>
      <path class="sfm-leg sfm-leg--l" d="M12 22.4v5.8"/>
      <path class="sfm-leg sfm-leg--r" d="M20 22.4v5.8"/>
    </g>`,

  /* lead — person + small rank blade */
  lead: `
    <g class="sfm-bob">
      <circle class="sfm-fill" cx="13.6" cy="8.2" r="4.8"/>
      <circle class="sfm-body" cx="13.6" cy="8.2" r="3.7"/>
      <path class="sfm-body" d="M7.6 19.8c1-3 3-4.5 6-4.5s5 1.5 6 4.5v1.6H7.6z"/>
      <path class="sfm-leg sfm-leg--l" d="M10.8 21.6v5.6"/>
      <path class="sfm-leg sfm-leg--r" d="M17.2 21.6v5.6"/>
      <path class="sfm-fill" d="M21.2 9.2 28 6.8l.9 2.4-6.6 2.4z"/>
      <path class="sfm-body" d="M21.6 9.7 27 7.7l.45 1.25-5.4 1.95z"/>
    </g>`,

  /* dispatch — rounded shield + check */
  dispatch: `
    <path class="sfm-fill" d="M16 3.6 26.4 7.6v6.8c0 5.4-3.9 9.6-10.4 11.8C9.5 24 5.6 19.8 5.6 14.4V7.6z"/>
    <path class="sfm-body" d="M16 5.2 24.6 8.4v5.4c0 4.4-3.2 7.8-8.6 9.6-5.4-1.8-8.6-5.2-8.6-9.6V8.4z"/>
    <path class="sfm-line" d="M11.4 15.2 14.6 18.2 21 11.4"/>`,

  /* bullet cam */
  camera: `
    <rect class="sfm-fill" x="4.2" y="9.6" width="16.2" height="12.8" rx="2.6"/>
    <rect class="sfm-body" x="5.4" y="10.8" width="13.8" height="10.4" rx="2"/>
    <circle class="sfm-fill" cx="12" cy="16" r="3.9"/>
    <circle class="sfm-body" cx="12" cy="16" r="2.7"/>
    <circle class="sfm-hi" cx="11" cy="15" r=".9"/>
    <path class="sfm-fill" d="M19.8 12.4 27.6 8.8v14.4l-7.8-3.6z"/>
    <path class="sfm-body" d="M20.4 13.2 26.2 10.4v11.2l-5.8-2.8z"/>`,

  /* PTZ dome */
  thermal: `
    <ellipse class="sfm-fill" cx="16" cy="12" rx="9" ry="5"/>
    <ellipse class="sfm-body" cx="16" cy="12" rx="7.4" ry="3.8"/>
    <path class="sfm-body" d="M9.8 14.2h12.4v6.6a2.4 2.4 0 0 1-2.4 2.4h-7.6a2.4 2.4 0 0 1-2.4-2.4z"/>
    <circle class="sfm-fill" cx="16" cy="18" r="3.4"/>
    <circle class="sfm-body" cx="16" cy="18" r="2.2"/>
    <circle class="sfm-hi" cx="15.1" cy="17.1" r=".8"/>`,

  /* siren — clean cone */
  siren: `
    <path class="sfm-fill" d="M9.4 25.6V14a6.6 6.6 0 0 1 13.2 0v11.6z"/>
    <path class="sfm-body" d="M11 24.8V14.2a5 5 0 0 1 10 0v10.6z"/>
    <rect class="sfm-body" x="7.4" y="24.8" width="17.2" height="2.4" rx="1"/>
    <path class="sfm-line sfm-blink" d="M16 5v2.8M7.6 9.4l1.8 1.5M24.4 9.4l-1.8 1.5"/>`,

  /* mesh mast */
  mast: `
    <rect class="sfm-fill" x="14.6" y="6" width="2.8" height="19.4" rx="1"/>
    <rect class="sfm-body" x="15.1" y="7" width="1.8" height="17.4" rx=".7"/>
    <path class="sfm-line" d="M9.6 25.2 16 11.2l6.4 14"/>
    <circle class="sfm-fill sfm-pulse" cx="16" cy="7" r="3.2"/>
    <circle class="sfm-body" cx="16" cy="7" r="2.1"/>
    <circle class="sfm-hi" cx="15.2" cy="6.2" r=".75"/>`,

  /* UAV — X body + props */
  drone: `
    <g class="sfm-hover">
      <path class="sfm-fill" d="M11.2 11.2 16 14.2 20.8 11.2 23.2 13.6 18.4 16.6 23.2 19.6 20.8 22 16 19 11.2 22 8.8 19.6 13.6 16.6 8.8 13.6z"/>
      <path class="sfm-body" d="M11.8 11.9 16 14.7l4.2-2.8 1.5 1.5-4 2.7 4 2.7-1.5 1.5L16 17.5l-4.2 2.8-1.5-1.5 4-2.7-4-2.7z"/>
      <circle class="sfm-fill" cx="16" cy="16" r="3.6"/>
      <circle class="sfm-body" cx="16" cy="16" r="2.5"/>
      <circle class="sfm-hi" cx="15.1" cy="15.1" r=".9"/>
      <g transform="translate(8.2 8.4)"><g class="sfm-prop sfm-spin">
        <ellipse class="sfm-body" cx="0" cy="0" rx="3" ry="1.05"/>
        <ellipse class="sfm-line" cx="0" cy="0" rx="1.05" ry="3"/>
      </g></g>
      <g transform="translate(23.8 8.4)"><g class="sfm-prop sfm-spin">
        <ellipse class="sfm-body" cx="0" cy="0" rx="3" ry="1.05"/>
        <ellipse class="sfm-line" cx="0" cy="0" rx="1.05" ry="3"/>
      </g></g>
      <g transform="translate(8.2 23.6)"><g class="sfm-prop sfm-spin">
        <ellipse class="sfm-body" cx="0" cy="0" rx="3" ry="1.05"/>
        <ellipse class="sfm-line" cx="0" cy="0" rx="1.05" ry="3"/>
      </g></g>
      <g transform="translate(23.8 23.6)"><g class="sfm-prop sfm-spin">
        <ellipse class="sfm-body" cx="0" cy="0" rx="3" ry="1.05"/>
        <ellipse class="sfm-line" cx="0" cy="0" rx="1.05" ry="3"/>
      </g></g>
    </g>`,

  droneHeat: `
    <g class="sfm-hover">
      <path class="sfm-body" d="M11.8 11.9 16 14.7l4.2-2.8 1.5 1.5-4 2.7 4 2.7-1.5 1.5L16 17.5l-4.2 2.8-1.5-1.5 4-2.7-4-2.7z"/>
      <circle class="sfm-fill" cx="16" cy="16" r="5"/>
      <circle class="sfm-body" cx="16" cy="16" r="3"/>
      <circle class="sfm-hi" cx="15" cy="15" r="1"/>
      <g transform="translate(8.2 8.4)"><g class="sfm-prop sfm-spin"><ellipse class="sfm-body" cx="0" cy="0" rx="2.8" ry="1"/></g></g>
      <g transform="translate(23.8 8.4)"><g class="sfm-prop sfm-spin"><ellipse class="sfm-body" cx="0" cy="0" rx="2.8" ry="1"/></g></g>
      <g transform="translate(8.2 23.6)"><g class="sfm-prop sfm-spin"><ellipse class="sfm-body" cx="0" cy="0" rx="2.8" ry="1"/></g></g>
      <g transform="translate(23.8 23.6)"><g class="sfm-prop sfm-spin"><ellipse class="sfm-body" cx="0" cy="0" rx="2.8" ry="1"/></g></g>
    </g>`,

  /* UGV chassis */
  ugv: `
    <g class="sfm-bob-soft">
      <rect class="sfm-fill" x="5.2" y="11.4" width="21.6" height="9.8" rx="2.2"/>
      <rect class="sfm-body" x="6.4" y="12.6" width="19.2" height="7.4" rx="1.5"/>
      <path class="sfm-body" d="M10.4 11.4h11.2V9a1.2 1.2 0 0 0-1.2-1.2h-8.8A1.2 1.2 0 0 0 10.4 9z"/>
      <g transform="translate(10.6 23.6)"><g class="sfm-wheel sfm-roll">
        <circle class="sfm-fill" r="2.9"/><circle class="sfm-body" r="2"/>
        <path class="sfm-line" d="M0-1.4v2.8M-1.4 0h2.8"/>
      </g></g>
      <g transform="translate(21.4 23.6)"><g class="sfm-wheel sfm-roll">
        <circle class="sfm-fill" r="2.9"/><circle class="sfm-body" r="2"/>
        <path class="sfm-line" d="M0-1.4v2.8M-1.4 0h2.8"/>
      </g></g>
    </g>`,

  ugvSig: `
    <g class="sfm-bob-soft">
      <rect class="sfm-fill" x="5.6" y="13" width="20.8" height="8.6" rx="1.8"/>
      <rect class="sfm-body" x="6.6" y="14" width="18.8" height="6.6" rx="1.3"/>
      <rect class="sfm-body" x="15" y="6.2" width="2" height="7.4" rx=".6"/>
      <circle class="sfm-fill sfm-pulse" cx="16" cy="5.4" r="2.4"/>
      <circle class="sfm-body" cx="16" cy="5.4" r="1.6"/>
      <g transform="translate(10.6 23.4)"><g class="sfm-wheel sfm-roll">
        <circle class="sfm-fill" r="2.6"/><circle class="sfm-body" r="1.8"/>
      </g></g>
      <g transform="translate(21.4 23.4)"><g class="sfm-wheel sfm-roll">
        <circle class="sfm-fill" r="2.6"/><circle class="sfm-body" r="1.8"/>
      </g></g>
    </g>`,

  /* job mark kept for non-map uses only */
  job: `
    <path class="sfm-fill" d="M16 4 27.4 26H4.6z"/>
    <path class="sfm-body" d="M16 6.8 24.8 24.6H7.2z"/>
    <rect class="sfm-body" x="14.8" y="11.6" width="2.4" height="6.6" rx="1"/>
    <circle class="sfm-body" cx="16" cy="20.8" r="1.3"/>`,
};

/* mono = plate corner type code · cls = spoken · motion = CSS motion family */
const G = {
  guard_foot: { title: "Foot patrol", mark: "person", shape: "circ", tone: "#3DE1C8", mono: "PTRL", cls: "Patrol", motion: "walk" },
  guard_lead: { title: "Patrol lead", mark: "lead", shape: "circ", tone: "#5FE8D0", mono: "LEAD", cls: "Lead", motion: "walk" },
  dispatcher: { title: "Dispatch", mark: "dispatch", shape: "circ", tone: "#7FD1FF", mono: "DISP", cls: "Dispatch", motion: "walk" },
  cam_bullet: { title: "Bullet cam", mark: "camera", shape: "sq", tone: "#8FA39E", mono: "CAM", cls: "Camera", motion: "scan" },
  cam_ptz_thermal: { title: "PTZ thermal", mark: "thermal", shape: "sq", tone: "#7EC8B8", mono: "THRM", cls: "Thermal cam", motion: "scan" },
  siren_horn: { title: "Siren", mark: "siren", shape: "tri", tone: "#FF9B5C", mono: "SIRN", cls: "Siren", motion: "alert" },
  mesh_gateway: { title: "Mesh gateway", mark: "mast", shape: "hex", tone: "#A78BFA", mono: "MESH", cls: "Gateway", motion: "beacon" },
  uav_scout: { title: "UAV scout", mark: "drone", shape: "oct", tone: "#A89BFF", mono: "UAV", cls: "UAV", motion: "air" },
  uav_thermal: { title: "UAV thermal", mark: "droneHeat", shape: "oct", tone: "#B48CFF", mono: "UAV·T", cls: "UAV thermal", motion: "air" },
  ugv_patrol: { title: "UGV patrol", mark: "ugv", shape: "rect", tone: "#D4A574", mono: "UGV", cls: "UGV", motion: "ground" },
  ugv_signal: { title: "UGV signal", mark: "ugvSig", shape: "rect", tone: "#E0A86A", mono: "UGV·S", cls: "UGV signal", motion: "ground" },
  job_flare: { title: "Job", mark: "job", shape: "tri", tone: "#FF6B6B", mono: "JOB", cls: "Job", motion: "job" },
  skill_observe: { title: "Observe", mark: "camera", shape: "circ", tone: "#7EC8B8", mono: "OBS", cls: "Observe", motion: "scan" },
  skill_thermal: { title: "Thermal", mark: "thermal", shape: "oct", tone: "#E8B86D", mono: "THRM", cls: "Thermal", motion: "scan" },
  skill_patrol: { title: "Patrol", mark: "person", shape: "circ", tone: "#3DE1C8", mono: "PTRL", cls: "Patrol", motion: "walk" },
  skill_challenge: { title: "Challenge", mark: "dispatch", shape: "circ", tone: "#7FD1FF", mono: "CHLG", cls: "Challenge", motion: "walk" },
  skill_alert: { title: "Alert", mark: "siren", shape: "tri", tone: "#FF9B5C", mono: "ALRT", cls: "Alert", motion: "alert" },
  skill_overwatch: { title: "Overwatch", mark: "drone", shape: "oct", tone: "#A89BFF", mono: "OVW", cls: "Overwatch", motion: "air" },
};

export const GLYPH_WORD = Object.fromEntries(
  Object.entries(G).map(([id, g]) => [id, g.title]),
);

export function resolveGlyphId(props) {
  if (!props) return "cam_bullet";
  if (props.glyph && G[props.glyph]) return props.glyph;
  /* Scene + skill weave (Soft Skill Weave) takes priority over heuristic IDs. */
  if (props.scene || props.skills) {
    const woven = glyphForSoftUnit(props);
    if (woven && G[woven]) return woven;
  }
  const id = String(props.id || props.label || "").toUpperCase();
  const skillsRaw = props.skills;
  const skills = Array.isArray(skillsRaw)
    ? skillsRaw.join(",")
    : String(skillsRaw || "");
  if (id.includes("SUP") || id.includes("DISPATCH")) return "dispatcher";
  if (id.includes("GUARD-B") || (id.includes("LEAD") && id.includes("GUARD"))) return "guard_lead";
  if (id.includes("GUARD") || props.kind === "person") {
    if (skills.includes("challenge") && !skills.includes("patrol")) return "dispatcher";
    if (skills.includes("alert") && skills.includes("challenge")) {
      return id.includes("A1") ? "guard_foot" : "guard_lead";
    }
    return "guard_foot";
  }
  if (id.includes("SIR") || id.includes("SIREN")) return "siren_horn";
  if (id.includes("GW") || id.includes("GATEWAY")) return "mesh_gateway";
  if (id.includes("CAM") && (id.includes("T2") || skills.includes("thermal"))) {
    return "cam_ptz_thermal";
  }
  if (id.includes("CAM") || (props.kind === "device" && skills.includes("observe"))) {
    return "cam_bullet";
  }
  if (props.kind === "device" && skills.includes("alert")) return "siren_horn";
  if (id.includes("UAV") && (skills.includes("thermal") || id.includes("L2"))) {
    return "uav_thermal";
  }
  if (props.kind === "drone" || id.includes("UAV")) return "uav_scout";
  if (id.includes("UGV") && (skills.includes("alert") || id.includes("R2"))) {
    return "ugv_signal";
  }
  if (props.kind === "robot" || id.includes("UGV")) return "ugv_patrol";
  if (props.kind === "incident") return "job_flare";
  return "cam_bullet";
}

function chromeOf(glyphId) {
  return G[glyphId] || G.cam_bullet;
}

/**
 * Prestige dual-tone emblem — always carries data-motion for idle silk.
 * Larger map sizes (26–30) so silhouette reads at fleet zoom.
 */
export function softMarkSvg(glyphId, size = 26) {
  const g = chromeOf(glyphId);
  const body = MARK[g.mark] || MARK.camera;
  const motion = g.motion || "scan";
  return `<svg class="sfmark" data-mark="${g.mark}" data-motion="${motion}" viewBox="0 0 32 32" width="${size}" height="${size}" fill="none" stroke="none" aria-hidden="true">${body}</svg>`;
}

/** List / fallback still exposes dual-tone marks. */
export function softGlyphSvg(kindOrGlyph, size = 16, forceGlyph = null) {
  let id = forceGlyph || kindOrGlyph;
  if (!G[id]) {
    if (kindOrGlyph === "person") id = "guard_foot";
    else if (kindOrGlyph === "device") id = "cam_bullet";
    else if (kindOrGlyph === "drone") id = "uav_scout";
    else if (kindOrGlyph === "robot") id = "ugv_patrol";
    else if (kindOrGlyph === "incident") id = "job_flare";
    else id = resolveGlyphId({ kind: kindOrGlyph, id: kindOrGlyph });
  }
  return softMarkSvg(id, size);
}

export function liveKindToGlyph(kind) {
  const k = String(kind || "").toLowerCase();
  if (k === "camera") return "cam_ptz_thermal";
  if (k === "siren") return "siren_horn";
  if (k === "gateway") return "mesh_gateway";
  if (k === "sensor") return "skill_observe";
  if (k === "contact") return "skill_challenge";
  if (k === "lidar") return "skill_thermal";
  return "cam_bullet";
}

export function softSkillGlyphSvg(skillId, size = 14) {
  const map = {
    observe: "skill_observe",
    thermal: "skill_thermal",
    patrol: "skill_patrol",
    challenge: "skill_challenge",
    alert: "skill_alert",
    overwatch: "skill_overwatch",
  };
  return softGlyphSvg(map[skillId] || "skill_observe", size, map[skillId] || "skill_observe");
}

function pinUsed(p) {
  if (!p) return false;
  const how = p.useHow || p.duty;
  if (how && how !== "available" && how !== "idle") return true;
  return p.used === 1 || p.free === 0 || p.free === false;
}

function isFixedProps(p) {
  if (!p) return false;
  return (
    p.fixed === 1 ||
    p.fixed === true ||
    p.mobile === 0 ||
    p.mobile === "0" ||
    p.kind === "device" ||
    p.profile === "static"
  );
}

function pinSig(p, open) {
  /* ETA / open / duty only — bearing continuous via theatre rAF (not DOM rewrite). */
  const etaStep = open
    ? Math.ceil((Number(p.eta) || 0) / 5) * 5
    : Math.ceil((Number(p.eta) || 0) / 15) * 15;
  return [
    p.id || "",
    p.used,
    p.useHow || "",
    p.missionTitle || "",
    etaStep,
    p.label || "",
    p.glyph || "",
    p.skills || "",
    p.color || "",
    p.road || "",
    p.responded || 0,
    p.fixed || 0,
    open ? 1 : 0,
  ].join("|");
}

function sameLl(a, b) {
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) < 1e-10 && Math.abs(a[1] - b[1]) < 1e-10;
}

function sameLlLoose(a, b) {
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) < 2e-6 && Math.abs(a[1] - b[1]) < 2e-6;
}

function softMapBusy(map) {
  if (!map) return false;
  if (map.__softCamBusy) return true;
  try {
    if (typeof map.isMoving === "function" && map.isMoving()) return true;
    if (typeof map.isZooming === "function" && map.isZooming()) return true;
    if (typeof map.isRotating === "function" && map.isRotating()) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function wireSoftCameraFreeze(map) {
  /* User cam ownership + freeze for DOM rewrite gating. */
  wireSoftUserCamGuard(map);
}

function makeSoftMarker(maplibregl, el, ll, offset) {
  return new maplibregl.Marker({
    element: el,
    anchor: "center",
    offset: offset || [0, 0],
    pitchAlignment: "viewport",
    rotationAlignment: "viewport",
    subpixelPositioning: true,
  }).setLngLat(ll);
}

function esc(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function shortName(props) {
  return String(props.label || props.id || "unit").trim();
}

/** Compact callsign for pin tags — drop filler words, keep key id. */
function callsign(props) {
  let s = shortName(props);
  s = s.replace(/\s+/g, " ").trim();
  /* "Cam T1-01" stays; strip long parentheticals */
  s = s.replace(/\([^)]*\)/g, "").trim();
  if (s.length > 14) s = s.slice(0, 13) + "…";
  return s || "unit";
}

function statusPack(props) {
  const used = pinUsed(props);
  const fixed = isFixedProps(props);
  const how = props.useHow || (used ? "enroute" : "available");
  const eta = Number(props.eta) || 0;
  let badge = "STANDBY";
  let short = "Standby";
  let reply = "Not dispatched";
  if (how === "enroute") {
    badge = eta > 0 ? `EN ROUTE · ${eta}s` : "EN ROUTE";
    short = eta > 0 ? `→ ${eta}s` : "En route";
    reply = "Responding · moving to job";
  } else if (how === "on_station") {
    badge = "COVERING";
    short = "Cover";
    reply = "Responded · fixed cover";
  } else if (how === "on_scene") {
    badge = "ON SCENE";
    short = "On scene";
    reply = "Responded · arrived";
  } else if (how === "available" || !used) {
    badge = "STANDBY";
    short = "Standby";
    reply = "Not dispatched · free";
  }
  return { how, used, fixed, badge, short, reply, eta };
}

/** Identity float: TYPE code first (animated), class name, who, status. No box. */
function idTagHtml(props, st, chrome, open) {
  const who = callsign(props);
  const what = chrome.cls || chrome.title || KIND_WORD[props.kind] || "Unit";
  const mono = String(chrome.mono || "UNIT").toUpperCase();
  const motion = chrome.motion || "scan";
  const job = props.missionTitle ? String(props.missionTitle).slice(0, 18) : "";
  const sub =
    mono === String(what).toUpperCase() || mono.includes(String(what).toUpperCase().slice(0, 3))
      ? ""
      : what;
  const meta = open
    ? st.badge
    : st.how === "enroute"
      ? st.short
      : st.used
        ? st.short
        : st.fixed
          ? "Planted"
          : "Ready";
  const plane =
    props.plane ||
    (st.fixed || props.kind === "device"
      ? "plant"
      : st.how === "enroute" || st.used
        ? "mission"
        : "mobile");
  const tier =
    plane === "mission"
      ? "Mission"
      : plane === "plant" || st.fixed
        ? "Plant"
        : "Unit";
  return `<span class="pin__tag pin__tag--soft sftoken__tag sftoken__id" data-how="${esc(st.how)}" data-kind="${esc(props.kind || "")}" data-plane="${esc(plane)}" data-motion="${esc(motion)}" data-fixed="${st.fixed ? "1" : "0"}" data-mobile="${st.fixed ? "0" : "1"}">
    <span class="sftoken__lane">${esc(tier)}</span>
    <span class="sftoken__typebar" data-motion="${esc(motion)}" data-how="${esc(st.how)}">
      <b class="sftoken__type">${esc(mono)}</b>
      ${sub ? `<em class="sftoken__code">${esc(sub)}</em>` : ""}
    </span>
    <span class="sftoken__id-row">
      <b class="sftoken__who">${esc(who)}</b>
    </span>
    <span class="sftoken__id-meta">
      <u class="sftoken__st">${esc(meta)}</u>
      ${job && st.used ? `<u class="sftoken__job">· ${esc(job)}</u>` : ""}
    </span>
  </span>`;
}

/** Smoothstep — no hard velocity zero at sim-tick seams (unlike ease-in-out cubic). */
function easeSilk(t) {
  return t * t * (3 - 2 * t);
}

function lerpAngle(a, b, t) {
  let d = ((b - a + 540) % 360) - 180;
  return (a + d * t + 360) % 360;
}

/**
 * Slightly longer than soft-fleet-sim TICK_MS (500) so each hop overlaps
 * and motion never rests between re-arms. Soft≠Live.
 */
const SILK_SPAN_MS = 640;

/** Human owns camera until this idle (15 min) — then AI tour takes over. */
export const SOFT_HUMAN_IDLE_MS = 15 * 60 * 1000;
/** How long AI rests on one unit/job before rotating. */
export const SOFT_AI_DWELL_MS = 9000;
const SOFT_AI_ZOOM_UNIT = 16.55;
const _SOFT_AI_ZOOM_JOB = 16.1;
const SOFT_AI_ZOOM_OVERVIEW = 15.2;
const SOFT_BOOT_MAX_ZOOM = 16.1;

function armMarkerLerp(mk, toLl, bearing, spanMs) {
  if (!mk || !toLl) return;
  const now = performance.now();
  let cur = mk.__lerpLl;
  if (!cur || cur.length < 2) {
    try {
      if (mk.getLngLat) {
        const L = mk.getLngLat();
        cur = [L.lng, L.lat];
      }
    } catch {
      cur = null;
    }
  }
  if (!cur) cur = toLl.slice();
  /* Re-arm from visible position only when target actually moved. */
  if (sameLlLoose(cur, toLl) && sameLlLoose(mk.__toLl, toLl)) {
    if (Number.isFinite(bearing)) {
      mk.__toBrg = bearing;
      if (!Number.isFinite(mk.__fromBrg)) mk.__fromBrg = mk.__brg || bearing;
    }
    return;
  }
  mk.__fromLl = [cur[0], cur[1]];
  mk.__toLl = toLl.slice();
  mk.__fromT = now;
  mk.__span = spanMs || SILK_SPAN_MS;
  if (Number.isFinite(bearing)) {
    mk.__fromBrg = Number.isFinite(mk.__brg) ? mk.__brg : bearing;
    mk.__toBrg = bearing;
  }
  mk.__ll = toLl.slice();
}

/** Flag map shell for CSS (kill pin transitions while MapLibre owns transform). */
export function setMapGesturingAttr(map, on) {
  if (!map) return;
  const v = on ? "1" : "0";
  try {
    const c = map.getContainer && map.getContainer();
    if (c) {
      c.setAttribute("data-gesturing", v);
      const host = c.closest && (c.closest(".map-host") || c.closest(".ops-map-shell"));
      if (host) host.setAttribute("data-gesturing", v);
      else if (c.parentElement) c.parentElement.setAttribute("data-gesturing", v);
    }
  } catch {
    /* */
  }
}

/** User pan/zoom ownership — auto priority framing must not fight fingers. */
export function wireSoftUserCamGuard(map) {
  if (!map || map.__softUserCamBound) return;
  map.__softUserCamBound = true;
  map.__softUserTs = Date.now();
  const noteUser = (e) => {
    if (e && e.originalEvent) {
      map.__softUserTs = Date.now();
      map.__softUserSteer = true;
      /* Operator back: pause AI tour until idle again. */
      map.__softAiMode = false;
      map.__softAiFocusKey = null;
    }
  };
  const clearBusy = (e) => {
    map.__softCamBusy = false;
    setMapGesturingAttr(map, false);
    if (e && e.originalEvent) {
      map.__softUserTs = Date.now();
      map.__softAiMode = false;
    }
    if (typeof map.__softResync === "function") map.__softResync();
  };
  const freeze = (e) => {
    noteUser(e);
    map.__softCamBusy = true;
    setMapGesturingAttr(map, true);
  };
  map.on("mousedown", noteUser);
  map.on("touchstart", noteUser);
  map.on("wheel", noteUser);
  map.on("movestart", freeze);
  map.on("zoomstart", freeze);
  map.on("rotatestart", freeze);
  map.on("pitchstart", freeze);
  map.on("moveend", clearBusy);
  map.on("zoomend", clearBusy);
  map.on("rotateend", clearBusy);
  map.on("pitchend", clearBusy);
}

function softHumanIdleMs(map) {
  return Date.now() - (map.__softUserTs || Date.now());
}

/** True when operator recently steered or locked a pin. */
export function softUserOwnsCam(map) {
  if (!map) return false;
  if (map.__softOpenId) return true;
  if (map.__softFocusHold) return true;
  if (map.__softAiMode) return false;
  return softHumanIdleMs(map) < SOFT_HUMAN_IDLE_MS;
}

function padBounds(coords, padM) {
  if (!coords.length) return null;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const c of coords) {
    if (!c || c.length < 2) continue;
    minX = Math.min(minX, c[0]);
    maxX = Math.max(maxX, c[0]);
    minY = Math.min(minY, c[1]);
    maxY = Math.max(maxY, c[1]);
  }
  if (!Number.isFinite(minX)) return null;
  const pad = padM != null ? padM : 0.00025;
  if (maxX - minX < 1e-6) {
    minX -= pad;
    maxX += pad;
  }
  if (maxY - minY < 1e-6) {
    minY -= pad;
    maxY += pad;
  }
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

/** Active enroute / covering units first; else all pins. (No incident pins.) */
export function softPriorityBounds(softGeo) {
  if (!softGeo) return null;
  const coords = [];
  const pins = (softGeo.pins && softGeo.pins.features) || [];
  for (const f of pins) {
    const p = f.properties || {};
    const c = f.geometry && f.geometry.coordinates;
    if (!c) continue;
    if (pinUsed(p) || p.useHow === "enroute" || p.useHow === "on_scene") coords.push(c);
  }
  if (coords.length < 1) {
    for (const f of pins) {
      const c = f.geometry && f.geometry.coordinates;
      if (c) coords.push(c);
    }
  }
  return padBounds(coords);
}

/**
 * Priority tour stops — committed units first (no map incident targets).
 * Soft roster only (this Soft sim / account theatre · never other tenants).
 */
export function softTourTargets(softGeo) {
  if (!softGeo) return [];
  const targets = [];
  const pins = (softGeo.pins && softGeo.pins.features) || [];
  for (const f of pins) {
    const p = f.properties || {};
    const c = f.geometry && f.geometry.coordinates;
    if (!c || !p.id) continue;
    const how = p.useHow || (pinUsed(p) ? "enroute" : "available");
    let rank = 9;
    if (how === "enroute") rank = 1;
    else if (how === "on_scene") rank = 2;
    else if (how === "on_station") rank = 3;
    else if (pinUsed(p)) rank = 4;
    else rank = 8;
    if (rank >= 8) continue; /* skip pure standby in tour — cuts clutter */
    targets.push({
      key: "a:" + p.id,
      id: p.id,
      kind: "unit",
      label: p.label || p.id,
      ll: [Number(c[0]), Number(c[1])],
      zoom: SOFT_AI_ZOOM_UNIT,
      rank,
      how,
    });
  }
  targets.sort((a, b) => a.rank - b.rank);
  /* Cap tour length so the map does not thrash */
  let list = targets.slice(0, 8);
  if (!list.length) {
    /* fallback: soft overview of all, as one stop, then 2 free units */
    const b = softPriorityBounds(softGeo);
    if (b) {
      list.push({
        key: "overview",
        id: "overview",
        kind: "overview",
        label: "Priority zone",
        ll: [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2],
        zoom: SOFT_AI_ZOOM_OVERVIEW,
        bounds: b,
        rank: 0,
      });
    }
    for (const f of pins) {
      if (list.length >= 4) break;
      const p = f.properties || {};
      const c = f.geometry && f.geometry.coordinates;
      if (!c || !p.id || pinUsed(p)) continue;
      list.push({
        key: "a:" + p.id,
        id: p.id,
        kind: "unit",
        label: p.label || p.id,
        ll: [Number(c[0]), Number(c[1])],
        zoom: SOFT_AI_ZOOM_UNIT,
        rank: 8,
        how: "available",
      });
    }
  }
  return list;
}

/**
 * Content gutters for fitBounds only.
 * Side-panel / chrome offset is already applied via map.setPadding
 * (ops-map-viewport.measureOpsMapPadding). Stacking right:420 here
 * double-counts the panel and shifts volume framing off-centre.
 */
export function softFrameContentPadding(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  if (o.padding && typeof o.padding === "object") return o.padding;
  return { top: 48, right: 48, bottom: 56, left: 48 };
}

function easeAiFrame(map, t) {
  if (!map || !t) return;
  map.__softCamProgram = true;
  map.__softFocusHold = false;
  const duration = t.kind === "overview" ? 1600 : 1400;
  try {
    if (t.bounds) {
      map.fitBounds(t.bounds, {
        padding: softFrameContentPadding(),
        maxZoom: t.zoom || SOFT_AI_ZOOM_OVERVIEW,
        duration,
        essential: true,
      });
    } else {
      map.easeTo({
        center: [Number(t.ll[0]), Number(t.ll[1])],
        zoom: t.zoom || SOFT_AI_ZOOM_UNIT,
        duration,
        essential: true,
        easing: (x) => 1 - Math.pow(1 - x, 3),
      });
    }
  } catch {
    /* */
  }
  window.setTimeout(() => {
    map.__softCamProgram = false;
  }, duration + 80);
}

/** Ease to a fixed unit / job — human click. Holds AI tour off. */
export function frameSoftFocus(map, ll, opts) {
  if (!map || !ll || ll.length < 2) return;
  const o = opts || {};
  const duration = o.duration != null ? o.duration : 920;
  let z = 16.55;
  try {
    z = Math.max(map.getZoom() || 15, o.zoom != null ? o.zoom : 16.55);
  } catch {
    /* */
  }
  z = Math.min(z, o.maxZoom != null ? o.maxZoom : 18.1);
  map.__softFocusHold = true;
  map.__softUserTs = Date.now();
  map.__softAiMode = false;
  map.__softAiFocusKey = null;
  map.__softCamProgram = true;
  try {
    map.easeTo({
      center: [Number(ll[0]), Number(ll[1])],
      zoom: z,
      duration,
      essential: true,
      easing: (t) => 1 - Math.pow(1 - t, 3),
    });
  } catch {
    /* */
  }
  window.setTimeout(() => {
    map.__softCamProgram = false;
  }, duration + 80);
}

/** Fit a volume polygon (or fall back to origin ease). Soft≠Live. */
export function frameSoftVolume(map, bounds, origin, opts) {
  if (!map) return;
  const o = opts || {};
  const duration = o.duration != null ? o.duration : 880;
  map.__softFocusHold = true;
  map.__softUserTs = Date.now();
  map.__softAiMode = false;
  map.__softAiFocusKey = null;
  map.__softCamProgram = true;
  /* Content gutters only — do not re-add side panel width (see softFrameContentPadding). */
  const pad = softFrameContentPadding(o);
  if (bounds && typeof map.fitBounds === "function") {
    try {
      map.fitBounds(bounds, {
        padding: pad,
        maxZoom: o.maxZoom != null ? o.maxZoom : 17.2,
        duration,
        essential: true,
        easing: (t) => 1 - Math.pow(1 - t, 3),
      });
      window.setTimeout(() => {
        map.__softCamProgram = false;
      }, duration + 80);
      return;
    } catch {
      /* fall through */
    }
  }
  if (origin) frameSoftFocus(map, origin, o);
  else {
    window.setTimeout(() => {
      map.__softCamProgram = false;
    }, 40);
  }
}

function softOutOfVolume(map, ll) {
  const vol = map && map.__softVolFocus;
  if (!vol || !vol.origin) return false;
  return !softLngLatInRadius(ll, vol.origin, vol.radiusM);
}

/** One-shot fit of priority work (boot / manual). Soft≠Live. */
export function softTryPriorityFrame(map, softGeo, opts) {
  const o = opts || {};
  if (!map || !softGeo) return false;
  if (!o.force) {
    if (softUserOwnsCam(map)) return false;
    if (softMapBusy(map)) return false;
  }
  const b = softPriorityBounds(softGeo);
  if (!b) return false;
  map.__softAutoTs = Date.now();
  map.__softFocusHold = false;
  map.__softCamProgram = true;
  try {
    map.fitBounds(b, {
      padding: softFrameContentPadding(o),
      maxZoom: o.maxZoom != null ? o.maxZoom : SOFT_BOOT_MAX_ZOOM,
      duration: o.duration != null ? o.duration : 1250,
      essential: true,
    });
  } catch {
    map.__softCamProgram = false;
    return false;
  }
  window.setTimeout(() => {
    map.__softCamProgram = false;
  }, (o.duration != null ? o.duration : 1250) + 80);
  return true;
}

/**
 * AI camera director:
 *  - human gestures win for SOFT_HUMAN_IDLE_MS (15 min)
 *  - after idle: AI mode cycles priority job → enroute units (de-crowds focus)
 *  - never frames foreign-tenant gear (Soft sim roster only)
 * Returns snapshot for supervision UI.
 */
export function tickSoftAiDirector(map, softGeo, opts) {
  const o = opts || {};
  if (!map || !softGeo) {
    return {
      mode: "off",
      remainMs: SOFT_HUMAN_IDLE_MS,
      focus: null,
      targets: 0,
      ownOnly: true,
    };
  }
  wireSoftUserCamGuard(map);
  const idle = softHumanIdleMs(map);
  const remainMs = Math.max(0, SOFT_HUMAN_IDLE_MS - idle);
  const openHold = !!(map.__softOpenId || map.__softFocusHold);

  /* 15 min no operator action: drop pin lock so AI may resume tour. */
  if (openHold && idle >= SOFT_HUMAN_IDLE_MS) {
    map.__softOpenId = null;
    map.__softFocusHold = false;
  }

  const pinHold = !!(map.__softOpenId || map.__softFocusHold);
  if (pinHold || (softMapBusy(map) && !map.__softCamProgram)) {
    const snap = {
      mode: pinHold ? "human-pin" : "human",
      remainMs,
      focus: map.__softOpenId
        ? { key: map.__softOpenId, id: map.__softOpenId, kind: "pin", label: "Pinned" }
        : null,
      targets: softTourTargets(softGeo).length,
      ownOnly: true,
      say: pinHold ? "Operator focused a pin · AI after 15 min idle" : "Camera busy",
    };
    map.__softDirectorSnap = snap;
    return snap;
  }

  if (idle < SOFT_HUMAN_IDLE_MS) {
    map.__softAiMode = false;
    map.__softAiFocusKey = null;
    const snap = {
      mode: "human",
      remainMs,
      focus: null,
      targets: softTourTargets(softGeo).length,
      ownOnly: true,
      say: "Human camera · AI tour after 15 min idle",
    };
    map.__softDirectorSnap = snap;
    return snap;
  }

  /* —— AI takeover (own Soft roster only · Soft≠Live) —— */
  map.__softAiMode = true;
  const targets = softTourTargets(softGeo);
  if (!targets.length) {
    const snap = {
      mode: "ai",
      remainMs: 0,
      focus: null,
      targets: 0,
      ownOnly: true,
      say: "AI on · no Soft targets",
    };
    map.__softDirectorSnap = snap;
    return snap;
  }

  const now = Date.now();
  let tour = map.__softAiTour;
  if (!tour || !Array.isArray(tour.keys) || tour.keys.join("|") !== targets.map((t) => t.key).join("|")) {
    tour = { i: -1, until: 0, keys: targets.map((t) => t.key) };
  }
  if (now >= (tour.until || 0) || o.forceStep) {
    tour.i = (tour.i + 1) % targets.length;
    tour.until = now + (o.dwellMs != null ? o.dwellMs : SOFT_AI_DWELL_MS);
    map.__softAiTour = tour;
    const t = targets[tour.i];
    map.__softAiFocusKey = t.key;
    map.__softAutoTs = now;
    easeAiFrame(map, t);
    if (typeof map.__softResync === "function") {
      try {
        map.__softResync();
      } catch {
        /* */
      }
    }
  } else {
    map.__softAiTour = tour;
  }

  const cur = targets[Math.max(0, tour.i)] || null;
  const snap = {
    mode: "ai",
    remainMs: 0,
    focus: cur
      ? { key: cur.key, id: cur.id, kind: cur.kind, label: cur.label, how: cur.how || "" }
      : null,
    dwellLeftMs: Math.max(0, (tour.until || 0) - now),
    targets: targets.length,
    tourIndex: tour.i,
    ownOnly: true,
    say: cur
      ? `AI tour · ${cur.kind} · ${cur.label}`
      : "AI tour",
  };
  map.__softDirectorSnap = snap;
  return snap;
}

/** Read-only supervision snapshot (panel). */
export function softDirectorSnapshot(map) {
  if (!map) {
    return { mode: "off", remainMs: SOFT_HUMAN_IDLE_MS, focus: null, targets: 0, ownOnly: true };
  }
  if (map.__softDirectorSnap) return map.__softDirectorSnap;
  const idle = softHumanIdleMs(map);
  return {
    mode: idle >= SOFT_HUMAN_IDLE_MS ? "ai" : "human",
    remainMs: Math.max(0, SOFT_HUMAN_IDLE_MS - idle),
    focus: null,
    targets: 0,
    ownOnly: true,
    say: "—",
  };
}

function softFocusKey(map) {
  if (!map) return null;
  if (map.__softOpenId) return map.__softOpenId;
  if (map.__softAiMode && map.__softAiFocusKey) return map.__softAiFocusKey;
  return null;
}

/**
 * Continuous silk theatre: unit motion + yaw between sim ticks.
 *
 * HARD RULE (must never regress — zoom/pan “pins run across the map”):
 * While the camera is busy, MapLibre alone owns marker screen position.
 * We must NOT interpolate setLngLat during gesturing — that double-applies
 * motion and looks like icons drift/slide under zoom.
 */
export function wireSoftInkTheatre(map, pool, opts) {
  if (!map || map.__softInkBound) return;
  map.__softInkBound = true;
  const getPool = (opts && opts.getPool) || (() => pool);
  let raf = 0;
  let lastPaintMs = 0;
  const reduced =
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;

  const step = (now) => {
    raf = requestAnimationFrame(step);
    /* Camera gesture · ride transform only — zero setLngLat thrash. */
    if (softMapBusy(map)) return;
    const p = getPool() || {};
    for (const k of Object.keys(p)) {
      if (!k.startsWith("a:")) continue;
      const mk = p[k];
      if (!mk || !mk.__toLl) continue;
      if (mk.__fixed) continue;
      const el = mk.getElement && mk.getElement();
      if (reduced) {
        try {
          mk.setLngLat(mk.__toLl);
          mk.__lerpLl = mk.__toLl.slice();
        } catch {
          /* */
        }
        continue;
      }
      const span = mk.__span || SILK_SPAN_MS;
      const t0 = mk.__fromT || now;
      let t = (now - t0) / span;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      const e = easeSilk(t);
      const a = mk.__fromLl || mk.__toLl;
      const b = mk.__toLl;
      const ll = [a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e];
      mk.__lerpLl = ll;
      try {
        mk.setLngLat(ll);
      } catch {
        /* */
      }
      if (el && Number.isFinite(mk.__toBrg)) {
        const brg = lerpAngle(mk.__fromBrg || mk.__toBrg, mk.__toBrg, e);
        mk.__brg = brg;
        el.style.setProperty("--brg", `${brg.toFixed(1)}deg`);
        const yaw = el.querySelector(".sftoken__yaw");
        if (yaw) yaw.style.transform = `translate(-50%,-50%) rotate(${brg.toFixed(1)}deg)`;
      }
    }
    /* Bloom geo thrash less often when idle (~8 fps is enough for breathe). */
    if (now - lastPaintMs < 120) return;
    lastPaintMs = now;
    try {
      const src = map.getSource && map.getSource("soft-heads");
      if (src && typeof src.setData === "function") {
        const feats = [];
        for (const k of Object.keys(p)) {
          if (!k.startsWith("a:")) continue;
          const mk = p[k];
          if (!mk || mk.__how !== "enroute") continue;
          const ll = mk.__lerpLl || mk.__toLl || mk.__ll;
          if (!ll) continue;
          feats.push({
            type: "Feature",
            properties: { id: k.slice(2), color: mk.__color || "#3DE1C8" },
            geometry: { type: "Point", coordinates: ll },
          });
        }
        src.setData({ type: "FeatureCollection", features: feats });
      }
      if (map.getLayer && map.getLayer("soft-heads-bloom")) {
        const phase = (now % 2200) / 2200;
        const breathe = 0.55 + 0.45 * Math.sin(phase * Math.PI * 2);
        map.setPaintProperty("soft-heads-bloom", "circle-radius", [
          "interpolate",
          ["linear"],
          ["zoom"],
          12,
          6 * breathe,
          16,
          14 * breathe,
          18,
          20 * breathe,
        ]);
      }
    } catch {
      /* */
    }
  };
  raf = requestAnimationFrame(step);
  map.__softInkRaf = () => {
    try {
      cancelAnimationFrame(raf);
    } catch {
      /* */
    }
  };
  map.__softInkStop = () => {
    try {
      cancelAnimationFrame(raf);
    } catch {
      /* */
    }
    map.__softInkBound = false;
  };
}

/** Tac-token shell — heading body for mobile, plant stem for fixed. Soft≠Live. */
export function buildSoftPinEl(props, opts) {
  const el = document.createElement("div");
  el.style.pointerEvents = "auto";
  el.setAttribute("role", "button");
  el.tabIndex = 0;
  fillPin(el, props, !!(opts && opts.open));
  return el;
}

function fillPin(el, props, open) {
  const st = statusPack(props);
  const name = shortName(props);
  const glyph = resolveGlyphId(props);
  const chrome = chromeOf(glyph);
  const role = chrome.title || KIND_WORD[props.kind] || props.kind || "unit";
  const what = chrome.cls || role;
  const mono = chrome.mono || "UNIT";
  const motion = chrome.motion || "scan";
  const skills = (props.skills || "").replace(/,/g, " · ") || "—";
  const profile =
    PROFILE_WORD[props.profile] ||
    props.profile ||
    (st.fixed ? "on station" : "mobile");
  const color = props.color || chrome.tone || "#7EC8B8";
  const job = props.missionTitle || "";
  const road = props.road || "";
  const brg = Number(props.bearing);
  const brgCss = Number.isFinite(brg) ? brg : 0;
  const mobile = !st.fixed && props.kind !== "device";
  const plane =
    props.plane ||
    (st.fixed || props.kind === "device"
      ? "plant"
      : st.how === "enroute" || st.used
        ? "mission"
        : "mobile");
  const mark = chrome.mark || "camera";
  const kind = props.kind || "device";

  el.dataset.open = open ? "1" : "0";
  el.dataset.on = open ? "1" : "0";
  el.dataset.soft = "1";
  el.dataset.glyph = glyph;
  el.dataset.kind = kind;
  el.dataset.mark = mark;
  el.dataset.motion = motion;
  el.dataset.cls = mono;
  el.dataset.fixed = st.fixed ? "1" : "0";
  el.dataset.mobile = mobile ? "1" : "0";
  el.dataset.plane = plane;
  el.dataset.how = st.how;
  el.dataset.busy = st.used ? "1" : "0";
  el.dataset.used = st.used ? "1" : "0";
  el.dataset.id = props.id || el.dataset.id || "";
  el.style.setProperty("--unit", color);
  el.style.setProperty("--kc", color);
  el.style.setProperty("--brg", `${brgCss}deg`);
  el.className =
    "pin sfpin sftoken sftoken--alive" +
    (mobile ? " sftoken--mobile" : " sftoken--fixed") +
    (st.used ? " sfpin--busy" : " sfpin--idle") +
    (st.fixed ? " sfpin--fixed" : " sfpin--mobile") +
    (open ? " sfpin--open" : "") +
    (st.how === "enroute" ? " sftoken--run" : "") +
    (st.how === "on_station" || st.how === "on_scene" ? " sftoken--live" : "") +
    ` sftoken--plane-${plane}` +
    ` sftoken--${motion}` +
    ` sftoken--k-${kind}`;
  el.style.zIndex =
    open ? "40" : plane === "mission" ? "16" : st.used ? "12" : st.fixed ? "4" : "8";

  const pathLine = road || (st.how === "enroute" ? profile : "");
  const kvRows = [
    job
      ? `<div class="sfsoft-kv"><em>Job</em><b>${esc(job)}</b></div>`
      : `<div class="sfsoft-kv"><em>Job</em><b class="sfsoft-mute">None · standby</b></div>`,
    `<div class="sfsoft-kv"><em>Who</em><b>${esc(name)}</b></div>`,
    `<div class="sfsoft-kv"><em>Type</em><b>${esc(what)} · ${esc(role)}</b></div>`,
    `<div class="sfsoft-kv"><em>Move</em><b>${esc(profile)}</b></div>`,
    pathLine
      ? `<div class="sfsoft-kv"><em>Path</em><b>${esc(pathLine)}</b></div>`
      : "",
    st.how === "enroute" && st.eta
      ? `<div class="sfsoft-kv"><em>ETA</em><b>${st.eta}s remaining</b></div>`
      : "",
    `<div class="sfsoft-kv"><em>Skills</em><b>${esc(skills)}</b></div>`,
    `<div class="sfsoft-kv sfsoft-kv--ink"><em>Ink</em><b><i style="background:${esc(color)}"></i> Path owns this colour</b></div>`,
  ]
    .filter(Boolean)
    .join("");

  const aura = `<i class="sftoken__aura" data-motion="${esc(motion)}" data-how="${esc(st.how)}" aria-hidden="true"></i>
    <i class="sftoken__aura sftoken__aura--lag" data-motion="${esc(motion)}" data-how="${esc(st.how)}" aria-hidden="true"></i>`;

  const plate = (size) =>
    `<div class="sftoken__plate" data-kind="${esc(kind)}" data-mark="${esc(mark)}" data-motion="${esc(motion)}" data-how="${esc(st.how)}" data-cls="${esc(mono)}" data-shape="${esc(chrome.shape)}">
       ${softMarkSvg(glyph, size)}
     </div>`;

  const body = mobile
    ? `<div class="sftoken__stack">
         <div class="sftoken__yaw" style="transform:translate(-50%,-50%) rotate(${brgCss}deg)">
           ${plate(32)}
           <i class="sftoken__tail" data-motion="${esc(motion)}" aria-hidden="true"></i>
         </div>
       </div>`
    : `<div class="sftoken__plant">
         ${plate(30)}
       </div>`;

  el.innerHTML = `
    <i class="sftoken__shadow" data-motion="${esc(motion)}" aria-hidden="true"></i>
    ${aura}
    <i class="sftoken__wave" data-how="${esc(st.how)}" data-motion="${esc(motion)}" aria-hidden="true"></i>
    <i class="sftoken__wave sftoken__wave--lag" data-how="${esc(st.how)}" data-motion="${esc(motion)}" aria-hidden="true"></i>
    ${body}
    ${idTagHtml(props, st, chrome, open)}
    ${
      open
        ? `<div class="callout map-plate soft-map-plate" style="--kc:${esc(color)}; --unit:${esc(color)}">
            <div class="callout__in">
              <u><span class="chip" data-tone="warn">SOFT</span> <span class="chip">${esc(mono)}</span> ${esc(st.badge)}</u>
              <div class="n"><b>${esc(name)}</b><i>${esc(what)}</i></div>
              <p>${esc(st.reply)}${job ? " · " + esc(job) : ""}</p>
              <div class="sfsoft-rows">${kvRows}</div>
              <p class="sfsoft-foot">Live type motion · Soft≠Live · click map to close</p>
            </div>
          </div>`
        : ""
    }`;

  el.setAttribute(
    "aria-label",
    `${name} · ${what} · ${st.badge}${job ? " · " + job : ""}${open ? " details open" : ""}`,
  );
  el.setAttribute("aria-expanded", open ? "true" : "false");
}

export function buildSoftIncidentEl(props, open) {
  const el = document.createElement("div");
  el.style.pointerEvents = "auto";
  el.setAttribute("role", "button");
  el.tabIndex = 0;
  fillIncident(el, props, !!open);
  return el;
}

function fillIncident(el, props, open) {
  const title = props.title || "Job";
  const needs = (props.needs || "").replace(/,/g, " · ") || "skills";
  const en = Number(props.enrouteN) || 0;
  const cover = Number(props.coverN) || 0;
  const scene = Number(props.sceneN) || 0;
  const teamRaw = String(props.team || "");
  const teamLines = teamRaw
    ? teamRaw
        .split("|")
        .filter(Boolean)
        .map((row) => {
          const [who, role, eta] = row.split(":");
          const roleTxt =
            role === "enroute"
              ? `en route${eta ? " " + eta : ""}`
              : role === "on_station"
                ? "covering"
                : role === "on_scene"
                  ? "on scene"
                  : role || "";
          return `<li><b>${esc(who)}</b> <em>${esc(roleTxt)}</em></li>`;
        })
        .join("")
    : "";
  const badge = en ? `${en} en route` : cover || scene ? "covered" : "open";
  const g = chromeOf("job_flare");

  el.dataset.open = open ? "1" : "0";
  el.dataset.on = open ? "1" : "0";
  el.dataset.soft = "1";
  el.dataset.sev = props.severity || "mid";
  el.dataset.id = props.id || "";
  el.dataset.how = "job";
  el.className =
    "pin sfpin sfincl sftoken sftoken--alive sftoken--job sftoken--alert" +
    (open ? " sfincl--open sfpin--open" : "");
  el.style.zIndex = open ? "42" : "12";
  el.style.setProperty("--kc", g.tone);
  el.style.setProperty("--unit", g.tone);
  el.dataset.motion = "job";
  el.dataset.mark = "job";

  const jobWho = title.length > 16 ? title.slice(0, 15) + "…" : title;
  el.innerHTML = `
    <i class="sftoken__shadow" data-motion="job" aria-hidden="true"></i>
    <i class="sftoken__aura" data-motion="job" data-how="job" aria-hidden="true"></i>
    <i class="sftoken__wave" data-how="job" data-motion="job" aria-hidden="true"></i>
    <i class="sftoken__wave sftoken__wave--lag" data-how="job" data-motion="job" aria-hidden="true"></i>
    <div class="sftoken__plant sftoken__plant--job">
      <div class="sftoken__plate" data-kind="incident" data-mark="job" data-motion="job" data-how="job" data-shape="tri" data-cls="JOB">
        ${softMarkSvg("job_flare", 32)}
      </div>
    </div>
    <span class="pin__tag pin__tag--soft sftoken__tag sftoken__id" data-how="job" data-kind="incident" data-motion="job">
      <span class="sftoken__typebar" data-motion="job" data-how="job">
        <b class="sftoken__type">JOB</b>
      </span>
      <span class="sftoken__id-row"><b class="sftoken__who">${esc(jobWho)}</b></span>
      <span class="sftoken__id-meta"><u class="sftoken__st">${esc(badge)}</u></span>
    </span>
    ${
      open
        ? `<div class="callout map-plate soft-map-plate soft-map-plate--job">
            <div class="callout__in">
              <u><span class="chip" data-tone="bad">SOFT JOB</span> ${esc(badge)}</u>
              <div class="n"><b>${esc(title)}</b><i>incident</i></div>
              <p>Needs ${esc(needs)}</p>
              <div class="sfsoft-rows">
                ${
                  props.place
                    ? `<div class="sfsoft-kv"><em>Where</em><b>${esc(props.place)}</b></div>`
                    : ""
                }
                <div class="sfsoft-kv"><em>Team</em><b>${en} en route · ${cover} cover · ${scene} on scene</b></div>
              </div>
              ${teamLines ? `<ul class="sfincl__team">${teamLines}</ul>` : ""}
              <p class="sfsoft-foot">Unit path colour = unit ink · Soft≠Live</p>
            </div>
          </div>`
        : ""
    }`;
}

function wireToggle(el, map, key, resync, getLl) {
  const act = (ev) => {
    try {
      ev.preventDefault();
      ev.stopPropagation();
    } catch {
      /* ignore */
    }
    if (!map) return;
    const opening = map.__softOpenId !== key;
    map.__softOpenId = opening ? key : null;
    map.__softUserTs = Date.now();
    if (!opening) {
      map.__softFocusHold = false;
    } else {
      let ll = null;
      try {
        ll = typeof getLl === "function" ? getLl() : null;
      } catch {
        ll = null;
      }
      if (ll && ll.length >= 2) {
        frameSoftFocus(map, ll, {
          zoom: key.startsWith("i:") ? 16.2 : 16.7,
          maxZoom: 18.1,
        });
      } else {
        map.__softFocusHold = true;
      }
    }
    if (typeof resync === "function") resync();
  };
  el.onclick = act;
  el.onkeydown = (ev) => {
    if (ev.key === "Enter" || ev.key === " ") act(ev);
  };
}

/**
 * Fixed devices lock LatLng for the map session.
 * 0×0 anchor + camera freeze: zoom/pan must not rewrite DOM mid-gesture.
 * Route tags + focus: who owns which path when multiple legs are drawing.
 */
export function syncSoftFleetMarkers(map, pool, softGeo, maplibregl, on = true) {
  if (!map || !maplibregl) return;
  if (!on || !softGeo) {
    Object.values(pool).forEach((mk) => mk.remove());
    for (const k of Object.keys(pool)) delete pool[k];
    applySoftRouteFocus(map, null);
    return;
  }

  wireSoftCameraFreeze(map);
  wireSoftInkTheatre(map, pool, { getPool: () => pool });
  if (!map.__softFixedLl) map.__softFixedLl = Object.create(null);

  if (map.__softMapCloseBound == null) {
    map.__softMapCloseBound = true;
    map.on("click", () => {
      if (map.__softOpenId) {
        map.__softOpenId = null;
        map.__softFocusHold = false;
        map.__softUserTs = Date.now();
        if (map.__softResync) map.__softResync();
      }
    });
  }

  const resync = () => syncSoftFleetMarkers(map, pool, softGeo, maplibregl, on);
  map.__softResync = resync;
  /* Hold softGeo for thaw after zoom — sim ticks while fingers still down. */
  map.__softGeoLast = softGeo;

  const busy = softMapBusy(map);
  const openId = map.__softOpenId || null;
  const focusKey = softFocusKey(map);
  const focusAsset =
    focusKey && focusKey.startsWith("a:")
      ? focusKey.slice(2)
      : openId && openId.startsWith("a:")
        ? openId.slice(2)
        : null;
  const hasFocus = !!(focusKey && focusKey !== "overview");
  const dimOthers = !!(hasFocus || (map.__softAiMode && map.__softAiFocusKey));

  /* Camera busy · HARD freeze geo:
     MapLibre alone moves screen x/y from last setLngLat.
     No armMarkerLerp · no setLngLat — that double-applies motion under zoom. */
  if (busy && Object.keys(pool).length) {
    const busyPins = (softGeo.pins && softGeo.pins.features) || [];
    for (const f of busyPins) {
      const pid = f.properties && f.properties.id;
      const id = "a:" + pid;
      const mk = pool[id];
      if (!mk) continue;
      const base = f.geometry && f.geometry.coordinates;
      if (!base || base.length < 2) continue;
      const p = f.properties || {};
      /* Stash intended sim hop for moveend silk catch-up — do not apply now. */
      if (!isFixedProps(p)) {
        mk.__pendingToLl = [Number(base[0]), Number(base[1])];
        if (Number.isFinite(Number(p.bearing))) mk.__pendingBrg = Number(p.bearing);
        mk.__color = p.color || mk.__color;
        mk.__how = p.useHow || mk.__how;
      } else if (map.__softFixedLl && !map.__softFixedLl[id]) {
        map.__softFixedLl[id] = [Number(base[0]), Number(base[1])];
      }
    }
    for (const k of Object.keys(pool)) {
      const mk = pool[k];
      const el = mk && mk.getElement && mk.getElement();
      if (!el) continue;
      if (k.startsWith("a:")) {
        const isOpen = openId === k;
        const isFocus = focusKey === k || (!!focusAsset && k === "a:" + focusAsset);
        const outVol = softOutOfVolume(map, mk.__lerpLl || mk.__ll);
        el.classList.toggle("sfpin--open", isOpen);
        el.classList.toggle("sfpin--focus", !!(isOpen || isFocus));
        el.dataset.on = isOpen || isFocus ? "1" : "0";
        el.classList.toggle("sfpin--dim", !!(dimOthers && !isFocus && !isOpen));
        el.classList.toggle("sfpin--out-vol", !!(outVol && !isFocus && !isOpen));
        el.classList.toggle("sftoken--ai-focus", !!(map.__softAiMode && isFocus));
      } else if (k.startsWith("r:")) {
        const aid = k.slice(2);
        const isFocus = focusAsset === aid;
        el.classList.toggle("sfroute--focus", isFocus);
        el.classList.toggle("sfroute--dim", !!(dimOthers && !isFocus));
      }
      /* Map soft incidents cancelled — drop any leftover i:* markers. */
      if (k.startsWith("i:")) {
        try {
          mk.remove();
        } catch {
          /* */
        }
        delete pool[k];
      }
    }
    applySoftRouteFocus(map, focusAsset);
    return;
  }

  const live = new Set();
  const pins = (softGeo.pins && softGeo.pins.features) || [];
  for (const f of pins) {
    const pid = f.properties && f.properties.id;
    const id = "a:" + pid;
    live.add(id);
    const base = f.geometry && f.geometry.coordinates;
    if (!base || base.length < 2) continue;
    const p = f.properties || {};
    const fixed = isFixedProps(p);

    let ll;
    if (fixed) {
      if (!map.__softFixedLl[id]) {
        map.__softFixedLl[id] = [Number(base[0]), Number(base[1])];
      }
      ll = map.__softFixedLl[id];
    } else {
      ll = [Number(base[0]), Number(base[1])];
    }

    const isOpen = openId === id;
    const isFocus = focusKey === id || (!!focusAsset && focusAsset === pid);
    const dim = !!(dimOthers && !isFocus && !isOpen);
    const outVol = !!(softOutOfVolume(map, ll) && !isFocus && !isOpen);
    const sig =
      pinSig(p, isOpen || isFocus) +
      (dim ? "|d" : "") +
      (isFocus ? "|f" : "") +
      (outVol ? "|v" : "");

    if (!pool[id]) {
      const el = buildSoftPinEl(p, { open: isOpen });
      if (dim) el.classList.add("sfpin--dim");
      if (outVol) el.classList.add("sfpin--out-vol");
      if (isOpen || isFocus) el.classList.add("sfpin--focus");
      if (map.__softAiMode && isFocus) el.classList.add("sftoken--ai-focus");
      el.dataset.on = isOpen || isFocus ? "1" : "0";
      const mk = makeSoftMarker(maplibregl, el, ll, [0, 0]).addTo(map);
      mk.__sig = sig;
      mk.__ll = ll.slice();
      mk.__lerpLl = ll.slice();
      mk.__fixed = fixed;
      mk.__brg = Number(p.bearing) || 0;
      mk.__toBrg = mk.__brg;
      mk.__color = p.color;
      mk.__how = p.useHow;
      pool[id] = mk;
      wireToggle(el, map, id, resync, () => {
        const live = pool[id];
        if (live && live.__lerpLl) return live.__lerpLl;
        if (live && live.__toLl) return live.__toLl;
        try {
          if (live && live.getLngLat) {
            const L = live.getLngLat();
            return [L.lng, L.lat];
          }
        } catch {
          /* */
        }
        return ll;
      });
    } else {
      const mk = pool[id];
      if (fixed) {
        if (!mk.__fixed || !sameLl(mk.__ll, ll)) {
          mk.setLngLat(ll);
          mk.__ll = ll.slice();
          mk.__lerpLl = ll.slice();
          mk.__fixed = true;
        }
      } else {
        /* Arm continuous lerp — never hard snap between sim ticks. */
        const target = mk.__pendingToLl || ll;
        const brg = Number.isFinite(mk.__pendingBrg) ? mk.__pendingBrg : Number(p.bearing);
        armMarkerLerp(mk, target, brg, SILK_SPAN_MS);
        mk.__pendingToLl = null;
        mk.__pendingBrg = null;
        mk.__fixed = false;
        mk.__color = p.color || mk.__color;
        mk.__how = p.useHow || mk.__how;
      }
      if (mk.__sig !== sig) {
        const el = mk.getElement();
        fillPin(el, p, isOpen);
        el.classList.toggle("sfpin--dim", dim);
        el.classList.toggle("sfpin--out-vol", outVol);
        el.classList.toggle("sfpin--focus", !!(isOpen || isFocus));
        el.classList.toggle("sftoken--ai-focus", !!(map.__softAiMode && isFocus));
        el.dataset.on = isOpen || isFocus ? "1" : "0";
        wireToggle(el, map, id, resync, () => {
          if (mk.__lerpLl) return mk.__lerpLl;
          if (mk.__toLl) return mk.__toLl;
          try {
            const L = mk.getLngLat();
            return [L.lng, L.lat];
          } catch {
            return ll;
          }
        });
        mk.__sig = sig;
        /* Restore yaw after DOM rewrite. */
        if (Number.isFinite(mk.__brg)) {
          el.style.setProperty("--brg", `${mk.__brg.toFixed(1)}deg`);
          const yaw = el.querySelector(".sftoken__yaw");
          if (yaw) yaw.style.transform = `translate(-50%,-50%) rotate(${mk.__brg.toFixed(1)}deg)`;
        }
      } else if (Number.isFinite(Number(p.bearing))) {
        mk.__toBrg = Number(p.bearing);
        if (!Number.isFinite(mk.__fromBrg)) mk.__fromBrg = mk.__toBrg;
      }
    }
  }

  /* Path ownership chips — only live leg mids; not camera-busy (handled above). */
  const routes = (softGeo.routes && softGeo.routes.features) || [];
  for (const f of routes) {
    const p = f.properties || {};
    if (p.leg && p.leg !== "live") continue;
    if (p.midLng == null || p.midLat == null) continue;
    const rid = "r:" + p.id;
    live.add(rid);
    const ll = [Number(p.midLng), Number(p.midLat)];
    const isFocus = focusAsset === p.id;
    const isMine = !dimOthers || isFocus;
    const etaStep = Math.ceil((Number(p.eta) || 0) / 10) * 10;
    const sig = [
      p.id,
      p.tag,
      p.color,
      p.profile,
      p.engine,
      p.missionTitle,
      etaStep,
      isMine ? 1 : 0,
      isFocus ? 1 : 0,
      dimOthers ? 1 : 0,
    ].join("|");
    if (!pool[rid]) {
      const el = document.createElement("div");
      el.className =
        "sfroute" + (isFocus ? " sfroute--focus" : "") + (isMine ? "" : " sfroute--dim");
      el.style.setProperty("--unit", p.color || "#7EC8B8");
      el.innerHTML = routePlateHtml(p);
      el.setAttribute("role", "button");
      el.tabIndex = 0;
      el.setAttribute(
        "aria-label",
        `Route ${p.label || p.id} · ${routeEngWord(p.engine, p.profile)}`,
      );
      wireToggle(el, map, "a:" + p.id, resync, () => {
        const unit = pool["a:" + p.id];
        if (unit && unit.__lerpLl) return unit.__lerpLl;
        if (unit && unit.__toLl) return unit.__toLl;
        try {
          if (unit && unit.getLngLat) {
            const L = unit.getLngLat();
            return [L.lng, L.lat];
          }
        } catch {
          /* */
        }
        return ll;
      });
      const mk = makeSoftMarker(maplibregl, el, ll, [0, -14]).addTo(map);
      mk.__sig = sig;
      mk.__ll = ll.slice();
      pool[rid] = mk;
    } else {
      const mk = pool[rid];
      if (!sameLlLoose(mk.__ll, ll)) {
        mk.setLngLat(ll);
        mk.__ll = ll.slice();
      }
      if (mk.__sig !== sig) {
        const el = mk.getElement();
        el.className =
          "sfroute" + (isFocus ? " sfroute--focus" : "") + (isMine ? "" : " sfroute--dim");
        el.style.setProperty("--unit", p.color || "#7EC8B8");
        el.innerHTML = routePlateHtml(p);
        wireToggle(el, map, "a:" + p.id, resync, () => {
          const unit = pool["a:" + p.id];
          if (unit && unit.__lerpLl) return unit.__lerpLl;
          if (unit && unit.__toLl) return unit.__toLl;
          try {
            if (unit && unit.getLngLat) {
              const L = unit.getLngLat();
              return [L.lng, L.lat];
            }
          } catch {
            /* */
          }
          return ll;
        });
        mk.__sig = sig;
      }
    }
  }

  /* Soft map incidents cancelled — do not draw job pins (sim job stays in panel). */

  for (const k of Object.keys(pool)) {
    if (!live.has(k) || k.startsWith("i:")) {
      pool[k].remove();
      delete pool[k];
    }
  }

  applySoftRouteFocus(map, focusAsset);
}

/**
 * Soft ink-theatre route stack on MapLibre.
 * Real OSM (walk/bike) + air corridor · Soft≠Live.
 * Layers paint unit colour; leg splits plan / travelled / remaining.
 */
export function installSoftRouteTheatre(map) {
  if (!map) return;
  if (!map.getSource("soft-routes")) {
    map.addSource("soft-routes", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }
  if (!map.getSource("soft-heads")) {
    map.addSource("soft-heads", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  const addLine = (id, filter, paint, layout) => {
    if (map.getLayer(id)) return;
    map.addLayer({
      id,
      type: "line",
      source: "soft-routes",
      ...(filter ? { filter } : {}),
      layout: { "line-cap": "round", "line-join": "round", ...(layout || {}) },
      paint,
    });
  };

  /* full plan ghost — faint basemap ink of whole dispatch */
  addLine(
    "soft-routes-trace",
    ["==", ["get", "leg"], "trace"],
    {
      "line-color": ["get", "color"],
      "line-opacity": 0.22,
      "line-width": 2.2,
      "line-dasharray": [1, 2.4],
    },
  );

  /* travelled wake — solid wider trail behind unit */
  addLine(
    "soft-routes-wake",
    ["==", ["get", "leg"], "wake"],
    {
      "line-color": ["get", "color"],
      "line-opacity": 0.55,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        12,
        3.5,
        16,
        7,
        18,
        10,
      ],
      "line-blur": 0.4,
    },
  );

  /* casing + glow + core only on live remaining path */
  const liveF = ["==", ["get", "leg"], "live"];
  addLine("soft-routes-case", liveF, {
    "line-color": "#04080a",
    "line-opacity": 0.78,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      12,
      6,
      16,
      11,
      18,
      14,
    ],
  });
  addLine("soft-routes-glow", liveF, {
    "line-color": ["get", "color"],
    "line-opacity": 0.38,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      12,
      10,
      16,
      20,
      18,
      26,
    ],
    "line-blur": 1.6,
  });
  addLine("soft-routes-core", liveF, {
    "line-color": ["get", "color"],
    "line-opacity": 0.95,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      12,
      2.4,
      16,
      3.8,
      18,
      5,
    ],
  });

  /* profile-specific marching flow on remaining legs */
  addLine(
    "soft-routes-flow-walk",
    ["all", liveF, ["==", ["get", "profile"], "walk"]],
    {
      "line-color": "#F4FFFE",
      "line-opacity": 0.92,
      "line-width": 1.7,
      "line-dasharray": [1.6, 1.1],
    },
  );
  addLine(
    "soft-routes-flow-road",
    [
      "all",
      liveF,
      ["any", ["==", ["get", "profile"], "bike"], ["==", ["get", "profile"], "drive"]],
    ],
    {
      "line-color": "#FFF6E8",
      "line-opacity": 0.95,
      "line-width": 1.9,
      "line-dasharray": [2.2, 0.9],
    },
  );
  addLine(
    "soft-routes-flow-air",
    ["all", liveF, ["==", ["get", "profile"], "air"]],
    {
      "line-color": "#F0EAFF",
      "line-opacity": 0.9,
      "line-width": 1.5,
      "line-dasharray": [1.0, 1.35],
    },
  );
  /* white spark — high-speed pulse along live path */
  addLine("soft-routes-spark", liveF, {
    "line-color": "#FFFFFF",
    "line-opacity": 0.72,
    "line-width": 1.1,
    "line-dasharray": [0.35, 3.4, 0.15, 0.0],
  });

  if (!map.getLayer("soft-heads-bloom")) {
    map.addLayer({
      id: "soft-heads-bloom",
      type: "circle",
      source: "soft-heads",
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          12,
          6,
          16,
          12,
          18,
          16,
        ],
        "circle-color": ["get", "color"],
        "circle-opacity": 0.22,
        "circle-blur": 0.85,
      },
    });
  }
  if (!map.getLayer("soft-heads-core")) {
    map.addLayer({
      id: "soft-heads-core",
      type: "circle",
      source: "soft-heads",
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          12,
          2.2,
          16,
          3.4,
          18,
          4.2,
        ],
        "circle-color": ["get", "color"],
        "circle-opacity": 0.85,
        "circle-stroke-color": "#041012",
        "circle-stroke-width": 1.2,
      },
    });
  }
}

export function softRouteLayerIds() {
  return [
    "soft-routes-trace",
    "soft-routes-wake",
    "soft-routes-case",
    "soft-routes-glow",
    "soft-routes-core",
    "soft-routes-flow-walk",
    "soft-routes-flow-road",
    "soft-routes-flow-air",
    "soft-routes-spark",
    "soft-heads-bloom",
    "soft-heads-core",
  ];
}

/** Dim non-focused soft paths; bold the unit you selected. */
export function applySoftRouteFocus(map, assetId) {
  if (!map) return;
  const layers = [
    ["soft-routes-trace", 0.22, 0.06, 2.2, 1.1],
    ["soft-routes-wake", 0.62, 0.12, 7, 2.6],
    ["soft-routes-case", 0.78, 0.16, 11, 4],
    ["soft-routes-glow", 0.38, 0.08, 20, 8],
    ["soft-routes-core", 0.95, 0.18, 3.8, 1.6],
    ["soft-routes-flow-walk", 0.92, 0.16, 1.7, 0.9],
    ["soft-routes-flow-road", 0.95, 0.16, 1.9, 1.0],
    ["soft-routes-flow-air", 0.9, 0.14, 1.5, 0.8],
    ["soft-routes-spark", 0.72, 0.1, 1.1, 0.6],
  ];
  for (const [layer, onOp, offOp, onW, offW] of layers) {
    if (!map.getLayer(layer)) continue;
    try {
      if (!assetId) {
        map.setPaintProperty(layer, "line-opacity", onOp);
        /* widths may be expression — skip reset for zoom-interpolated layers */
        if (
          layer === "soft-routes-flow-walk" ||
          layer === "soft-routes-flow-road" ||
          layer === "soft-routes-flow-air" ||
          layer === "soft-routes-spark" ||
          layer === "soft-routes-trace"
        ) {
          map.setPaintProperty(layer, "line-width", onW);
        }
        continue;
      }
      const isGlow = layer === "soft-routes-glow";
      const isWake = layer === "soft-routes-wake";
      const hotOp = isGlow ? 0.62 : isWake ? 0.88 : 1;
      const hotW =
        layer === "soft-routes-case" ? 13 : isGlow ? onW + 6 : isWake ? onW + 3 : onW + 1.2;
      map.setPaintProperty(layer, "line-opacity", [
        "case",
        ["==", ["get", "id"], assetId],
        hotOp,
        offOp,
      ]);
      if (
        layer === "soft-routes-flow-walk" ||
        layer === "soft-routes-flow-road" ||
        layer === "soft-routes-flow-air" ||
        layer === "soft-routes-spark" ||
        layer === "soft-routes-trace" ||
        layer === "soft-routes-core"
      ) {
        map.setPaintProperty(layer, "line-width", [
          "case",
          ["==", ["get", "id"], assetId],
          hotW,
          offW,
        ]);
      }
    } catch {
      /* paint optional */
    }
  }
}

/**
 * Fine-step ant-path sequences (Brody / MapLibre march).
 * 4-step tables looked stuttery — dense samples yield silk. Soft≠Live.
 */
function antDashSequence(dash, gap, precision = 0.08) {
  const seq = [];
  for (let i = 0; i <= dash + 1e-9; i += precision) {
    seq.push([i, gap, Math.max(0, dash - i)]);
  }
  for (let i = precision; i <= gap + 1e-9; i += precision) {
    seq.push([0, i, dash, Math.max(0, gap - i)]);
  }
  return seq;
}

export const SOFT_ANT_WALK = antDashSequence(1.6, 1.1, 0.07);
export const SOFT_ANT_ROAD = antDashSequence(2.2, 0.9, 0.07);
export const SOFT_ANT_AIR = antDashSequence(1.0, 1.35, 0.06);
export const SOFT_ANT_SPARK = antDashSequence(0.32, 3.2, 0.08);
/* Back-compat names */
export const SOFT_DASH_WALK = SOFT_ANT_WALK;
export const SOFT_DASH_BIKE = SOFT_ANT_ROAD;
export const SOFT_DASH_AIR = SOFT_ANT_AIR;

export function raiseSoftRouteLayers(map) {
  if (!map) return;
  for (const id of softRouteLayerIds()) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      /* ignore */
    }
  }
}

function routeEngWord(engine, profile) {
  const e = String(engine || "").toLowerCase();
  const p = String(profile || "").toLowerCase();
  if (p === "air" || e.includes("air")) return "Air corridor";
  if (e.includes("osrm") || e.includes("osm")) return "OSM road";
  if (p === "walk") return "Foot · OSM";
  if (p === "bike") return "Path · OSM";
  if (p === "drive") return "Road · OSM";
  return "Route";
}

function routePlateHtml(p) {
  const who = String(p.tag || p.label || p.id || "unit");
  const job = String(p.missionTitle || "").slice(0, 26);
  const eng = routeEngWord(p.engine, p.profile);
  const road = String(p.road || "").trim();
  const eta = Number(p.eta) || 0;
  const etaTxt = eta > 0 ? `ETA ${eta}s` : "En route";
  const prof =
    p.profile === "air"
      ? "AIR"
      : p.profile === "walk"
        ? "WALK"
        : p.profile === "drive"
          ? "ROAD"
          : p.profile === "bike"
            ? "UGV"
            : String(p.profile || "PATH").toUpperCase();
  return `<span class="sfroute__plate" data-profile="${esc(p.profile || "")}">
    <span class="sfroute__head">
      <b>${esc(who)}</b>
      <em class="sfroute__mono">${esc(prof)}</em>
    </span>
    <span class="sfroute__sub">
      <i>${esc(eng)}${road ? " · " + esc(road.slice(0, 18)) : ""}</i>
      <u>${esc(etaTxt)}</u>
    </span>
    ${job ? `<span class="sfroute__job">${esc(job)}</span>` : ""}
  </span>`;
}
